import {
	Button,
	Flex,
	Input,
	Typography,
	ProductCard,
	ProgressiveDiscountCards,
	OrderBy,
} from 'front-commons/ds';
import { SelectLabelValue } from 'front-commons/ds/components/Select/interfaces';
import { Range } from 'front-commons/entities/Product/Range';
import { useDebounce } from 'front-commons/hooks';
import { Icon } from 'hype-ds';
import { useEffect, useMemo, useState } from 'react';
import AddToBasketButton from 'containers/AddToBasketButton';
import { DistributorsProps } from 'services/products/interfaces';
import {
	getCurrentComponentRange,
	handlerDistributorValidity,
	hasQuantityChanged as handleHasQuantityChanged,
	hasUnableToUpdate,
} from 'shared/promotionDrawer';
import useCustomer from 'stores/customer';
import useDrawer from 'stores/drawer';
import usePos from 'stores/pos';
import { PromotionDetailsDrawerProps } from './interfaces';
import Skeletons from './Skeletons';
import { FooterPromotionDetails, Wrapper, ProductCards, SearchInputContainer, ButtonsContainer } from './styles';
import { UnavailableAlert } from './components/UnavailableAlert';
import { useNavigate } from 'react-router-dom';
import { PromotionDetailsDescription } from './components/PromotionDetailsDescription';
import { PromotionDetailsDescriptionProps } from './components/PromotionDetailsDescription/interfaces';
import { PromoDetailsCardProps } from 'front-commons/ds/components/ProductCard/variants/PromoDetailsCard/interfaces';
import { OrderSummary } from './components/OrderSummary';

export default function PromotionDetails({
	data,
	basketData,
	promotionId,
	recoveryLastDistributor,
	distributorWaiting,
	summary,
	loadingPromotion,
	loadingBasket,
	productsInComboPrices,
	brute,
	tax,
	discount,
	liquid,
	selectedDistributor,
	updatedProductsInCombo,
	flexAddLoading,
	notFoundProducts,
	sourceProductId,
	CTAComponent,
	posId,
	disableDistributorSelector,
	shouldShowChangeDistributorsButton,
	dispatchGTMBasketEvent,
	getProductValidity,
	getFlexQuantityInBasket,
	updateSummary,
	handleChangeDistributor,
	handleRemovePromotion,
	handleAddToBasketClick,
	getSearchedProducts,
	handleSortItems,
	getProductsInBasketByType,
	onAfterBasketUpdate,
}: PromotionDetailsDrawerProps) {
	const navigate = useNavigate();

	const [searchValue, setSearchValue] = useState('');
	const [sortSelected, setSortSelected] = useState<SelectLabelValue | undefined>();

	const { handleHasPermission } = useCustomer();
	const { handleOpenDrawer, isDrawerOpen } = useDrawer();

	const { posStore } = usePos();
	const pos = posId ? { id: posId } : posStore.selectedPos;

	const hasPermission = handleHasPermission('BASKET_EDIT');

	const greaterProductInComboAmount = data.productsInCombo?.map((child) => child.quantity).sort((a, b) => b - a)?.[0];

	const maxFixedQuantity =
		data.fixedAmount && greaterProductInComboAmount ? Math.floor(99999 / greaterProductInComboAmount) : undefined;

	const hasValidDistributors = handlerDistributorValidity(data);
	const hasQuantityChanged = handleHasQuantityChanged(summary, promotionId, basketData) && !data?.fixedAmount;
	const debouncedValue = useDebounce(searchValue, 400);

	const productsInCombo = useMemo(() => {
		const foundedProduct = data.productsInCombo?.find(({ productId }) => productId === sourceProductId);

		if (sortSelected?.value || searchValue || !sourceProductId || !foundedProduct) return data.productsInCombo;

		return [foundedProduct, ...(data.productsInCombo?.filter(({ productId }) => productId !== sourceProductId) || [])];
	}, [sourceProductId, searchValue, sortSelected, data.productsInCombo]);

	const addedProductsInCombo = useMemo(
		() => updatedProductsInCombo.filter((item) => item.quantity > 0),
		[updatedProductsInCombo],
	);

	const allProductsQuantity = useMemo(() => {
		const foundedProductsQuantity = summary.reduce((acc, curr) => {
			if (curr.quantity) {
				return acc + curr.quantity;
			}

			return acc;
		}, 0);

		return foundedProductsQuantity;
	}, [summary]);

	const currentQuantityInBasket = useMemo(
		() => basketData?.baskets?.flatMap(({ products }) => products).find(({ id }) => id === promotionId)?.quantity,
		[basketData, data, promotionId],
	);

	const currentBasketDistributor = data?.distributors?.find(({ distributorId }) => {
		return (
			distributorId ===
			basketData?.baskets?.find(({ products }) => products.find(({ id }) => id === promotionId))?.distributorId
		);
	});

	const addToCartLabel = useMemo(() => {
		if (data.minQuantityDistinctProducts) {
			if (addedProductsInCombo.length >= data.minQuantityDistinctProducts) return 'Adicionar ao carrinho';

			return 'Promoção bloqueada';
		}

		if (currentQuantityInBasket) return 'Atualizar';

		return 'Adicionar';
	}, [data.minQuantityDistinctProducts, currentQuantityInBasket, addedProductsInCombo.length]);

	const showProducts = loadingPromotion !== 'refetch' && !loadingBasket && !notFoundProducts;
	const showProgressiveDiscount = !!data?.ranges?.length && !data?.progressiveDiscount;
	const showUnavailableAlert = !data?.distributors?.length || !hasValidDistributors;

	const productsLeftToUnlockPromotion =
		!!data.minQuantityDistinctProducts && addedProductsInCombo.length < data.minQuantityDistinctProducts
			? addedProductsInCombo.length
			: data.minQuantityDistinctProducts;

	const handleRedirectToDistributorsPage = () => {
		navigate(`/minhas-farmacias/distribuidores`, { state: { posId } });
	};

	useEffect(() => {
		getSearchedProducts(promotionId, pos.id, searchValue);
	}, [debouncedValue]);

	useEffect(() => {
		if (promotionId) {
			setSearchValue('');
			setSortSelected(undefined);
		}
	}, [promotionId]);

	useEffect(() => {
		setSortSelected({
			value: '',
			label: 'Selecionar',
		});
	}, [allProductsQuantity]);

	return (
		<Wrapper className="wrapper-drawer" direction="column" gap="12px">
			{['full', 'opening'].includes(loadingPromotion) ? (
				<Skeletons />
			) : (
				<>
					<Flex padding="1rem" gap="1rem" direction="column" flex="1">
						<PromotionDetailsDescription
							data={data as PromotionDetailsDescriptionProps['data']}
							disableDistributorSelector={disableDistributorSelector}
							distributorWaiting={distributorWaiting}
							initialDistributor={currentBasketDistributor as DistributorsProps}
							onChange={handleChangeDistributor as PromotionDetailsDescriptionProps['onChange']}
							recoveryLastDistributor={recoveryLastDistributor}
						/>

						{showUnavailableAlert && (
							<UnavailableAlert
								promotionId={promotionId}
								canChangeDistributors={!!shouldShowChangeDistributorsButton && !!data.hasOtherAvailableDistributor}
								onRedirectToDistributors={!!CTAComponent ? handleRedirectToDistributorsPage : undefined}
							/>
						)}

						<SearchInputContainer>
							<Input
								label="Itens do combo"
								name="filter_product"
								onChange={(el) => {
									el.stopPropagation();
									setSearchValue(el.target.value);
								}}
								value={searchValue}
								placeholder="Buscar Pelo Nome ou EAN"
								width="100%"
								size="small"
								leftIcon={{ name: 'search' }}
								data-component-name="promotion-search"
							/>

							<OrderBy
								options={[
									{
										value: '1',
										label: 'Maior preço',
									},
									{
										value: '2',
										label: 'Menor preço',
									},
									{
										value: '3',
										label: 'Ordem alfabética',
									},
									{
										value: '4',
										label: 'Produtos já selecionados',
									},
									{
										value: '',
										label: 'Selecionar',
										hidden: true,
									},
								]}
								value={sortSelected?.value}
								onChange={(e) => {
									const { name, value } = e.currentTarget;

									setSortSelected({ label: name, value });
									handleSortItems(Number(value));
								}}
							/>
						</SearchInputContainer>

						{(loadingPromotion === 'refetch' || loadingBasket) && <Skeletons.ProductCards />}

						{notFoundProducts && (
							<Flex direction="column" gap="0px" padding={{ small: '12px 16px', medium: '12px 24px' }} flex="1">
								<Flex alignItems="center" justifyContent="center" direction="column" gap="8px" flex="1">
									<Icon name="deployed_code_alert" size="48px" />
									<Typography>Nenhum produto encontrado</Typography>
								</Flex>
							</Flex>
						)}

						{showProducts && (
							<ProductCards>
								{productsInCombo?.map((item) => (
									<ProductCard
										variant="PROMO_DETAILS"
										imageProvider={import.meta.env.VITE_STATIC_STORAGE_BASE}
										hasPermission={hasPermission}
										key={item.productId}
										product={item as PromoDetailsCardProps['product']}
										// TODO: adjust typing basketData={basketData as PromoDetailsCardProps['basketData']}
										basketData={basketData as any}
										promotionId={promotionId}
										hasRangeProduct={data.hasRangeProduct}
										mixedRanges={data?.ranges as PromoDetailsCardProps['mixedRanges']}
										fixedAmount={data.fixedAmount}
										rangeByQuantity={data.rangeByQuantity}
										quantityInBasket={currentQuantityInBasket}
										progressiveDiscount={data.progressiveDiscount}
										progressiveDiscountProductMix={data.progressiveDiscountProductMix}
										hasValidDistributors={hasValidDistributors}
										productsInComboPrices={productsInComboPrices}
										summary={summary}
										currentSubtotal={brute}
										updateSummary={updateSummary}
										getFlexQuantityInBasket={getFlexQuantityInBasket}
										getProductValidity={getProductValidity}
										allProductsQuantity={allProductsQuantity}
										posId={posId}
										onOpenDrawer={handleOpenDrawer}
										getProductsInBasketByType={
											getProductsInBasketByType as PromoDetailsCardProps['getProductsInBasketByType']
										}
										selectedDistributor={selectedDistributor}
										industryName={data.industryName}
									/>
								))}
							</ProductCards>
						)}
					</Flex>

					<FooterPromotionDetails>
						{showProgressiveDiscount && (
							<Flex margin={{ small: '0 -2px', medium: '0' }}>
								<ProgressiveDiscountCards
									data={{
										type: data?.rangeByQuantity ? 'quantity' : 'currency',
										values: data?.ranges as Range[],
									}}
									atualValue={getCurrentComponentRange(
										data?.rangeByQuantity,
										data?.progressiveDiscount,
										summary,
										currentQuantityInBasket,
									)}
									rangeByQuantity={data?.rangeByQuantity}
								/>
							</Flex>
						)}
						<OrderSummary discount={discount} taxSubstitution={tax} finalPrice={liquid} price={brute} />
						<Flex gap="8px" width="100%" justifyContent="space-between" alignItems="center" wrap="wrap">
							{CTAComponent && CTAComponent}

							{!CTAComponent && (
								<>
									{data.availableLimit && data.availableLimit < (currentQuantityInBasket || 0) && (
										<Flex
											maxWidth="339px"
											as={Typography}
											color="--semantic-info-text"
											style={{ textAlign: 'start' }} // due to a higher CSS specify rule
											variant="ParagraphSmall/Regular"
										>
											Reduza a quantidade solicitada para conseguir comprar essa promoção.
										</Flex>
									)}

									{!!data.minQuantityDistinctProducts && (
										<Typography variant="Paragraph/XS" whiteSpace={{ medium: 'nowrap' }}>
											<Typography as="span" variant="Label/XS">
												{productsLeftToUnlockPromotion}/{data.minQuantityDistinctProducts}
											</Typography>{' '}
											Produtos para desbloquear
										</Typography>
									)}

									<ButtonsContainer>
										{!!currentQuantityInBasket && (
											<Button
												width={{ small: '100%', medium: '126px' }}
												variant="outlined"
												onClick={handleRemovePromotion}
												hasPermission={hasPermission}
											>
												Remover
											</Button>
										)}

										{data?.fixedAmount && (
											<AddToBasketButton
												label="Adicionar ao carrinho"
												leftIcon={{ size: '20px', name: 'shopping_cart' }}
												posId={pos.id}
												hasPermission={hasPermission}
												disabled={!hasValidDistributors || data.availableLimit === 0}
												productId={promotionId}
												distributorId={selectedDistributor?.distributorId}
												counterOptions={{ width: '100%', maxWidth: '160px', hasPermission }}
												productsInCombo={updatedProductsInCombo}
												initialQuantity={currentQuantityInBasket}
												onUpdateBasketType={({ type }) => {
													dispatchGTMBasketEvent(type);
													onAfterBasketUpdate?.();
												}}
												maxQuantity={Math.min(data.availableLimit ?? 99999, maxFixedQuantity ?? 99999)}
												disableAfterAdd
												suggestionGroupId={data?.suggestionGroupId}
												suggestionGroupName={data?.suggestionGroupName}
											/>
										)}

										{!data?.fixedAmount && (
											<Button
												width={{
													small: '100%',
													medium:
														(['Adicionar ao carrinho'].includes(addToCartLabel) && '240px') ||
														(['Promoção bloqueada'].includes(addToCartLabel) && '200px') ||
														'160px',
												}}
												onClick={handleAddToBasketClick}
												loading={flexAddLoading}
												disabled={hasUnableToUpdate({
													hasQuantityChanged,
													currentQuantityInBasket,
													hasValidDistributors,
													minQuantityDistinctProducts: data.minQuantityDistinctProducts,
													addedProductsInComboUnits: addedProductsInCombo.length,
												})}
												leftIcon={{
													name: addToCartLabel === 'Promoção bloqueada' ? 'lock' : 'shopping_cart',
													color: addToCartLabel === 'Promoção bloqueada' ? '--text-disable' : '--text-invert',
													detached: false,
													size: '20px',
												}}
												hasPermission={hasPermission}
												whiteSpace="nowrap"
											>
												{addToCartLabel}
											</Button>
										)}
									</ButtonsContainer>
								</>
							)}
						</Flex>
					</FooterPromotionDetails>
				</>
			)}
		</Wrapper>
	);
}
